import { SUPPORTED_LOCALES, useI18n } from '@/i18n/i18n-provider';

/**
 * A representative flag per locale, keyed off the same country its
 * `defaultCurrency` already implies (`packages/i18n/src/locale-catalog.ts`) —
 * not a claim that the language belongs to one country, just a recognisable
 * mark next to a script someone may not read yet. Kept here, not in the
 * shared catalog: it's a presentational detail of this one picker, not a
 * fact the backend has any use for.
 */
const FLAGS: Record<string, string> = {
  en: '🇺🇸',
  'zh-CN': '🇨🇳',
  hi: '🇮🇳',
  es: '🇪🇸',
  ar: '🇸🇦',
  fr: '🇫🇷',
  bn: '🇧🇩',
  'pt-BR': '🇧🇷',
  ru: '🇷🇺',
  ur: '🇵🇰',
};

/**
 * Language selector.
 *
 * Each option is labelled in its own script. A Bengali speaker looking for
 * their language scans for "বাংলা", not for "Bengali" — listing them in English
 * makes the picker useless to exactly the people who need it.
 */
export function LocalePicker() {
  const { locale, setLocale, t } = useI18n();

  return (
    <label className="locale-picker">
      <span className="sr-only">{t('common.labels.language', { fallback: 'Language' })}</span>
      <select
        value={locale}
        onChange={(event) => setLocale(event.target.value)}
        className="locale-select"
      >
        {SUPPORTED_LOCALES.map((entry) => (
          // `dir` per option so a right-to-left name renders correctly inside a
          // left-to-right page, and the reverse. The flag stays in visual LTR
          // order either way — it's a glyph, not text to mirror.
          <option key={entry.code} value={entry.code} dir={entry.direction}>
            {FLAGS[entry.code]} {entry.nativeName}
          </option>
        ))}
      </select>
    </label>
  );
}
